import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Box, Tooltip, Typography } from '@mui/material'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import { socketEventEmitter } from '../socket'
import { useSidebar } from '../context/SidebarContext'
import { useProject } from '../context/ProjectContext'
import TaskService from '../services/TaskService'
import ProjectService from '../services/ProjectService'
import TacticIcon from 'assets/iconComponents/TacticIcon'
import isPrototypesEnabled from '../utils/prototypesEnabled'
import { useAuth } from '../context/AuthContext'

const Sidebar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { collapsedSidebar } = useSidebar()
  const { user } = useAuth()
  const { selectedProject, setSelectedProject } = useProject()
  const [, setTasks] = useState([])
  const [processingTasksTotal, setProcessingTasksTotal] = useState(null)

  const prototypesEnabled = isPrototypesEnabled(user?.email)
  const menuItems = [
    { path: '/dashboard', id: 'dashboardMenuItem', label: 'Dashboard', icon: <DashboardOutlinedIcon />, tooltip: 'Review your QA results' },
    { path: '/pages', id: 'pagesMenuItem', label: 'Pages', icon: <FactCheckOutlinedIcon />, tooltip: 'Test simple page flows' },
    {
      path: '/test-journeys',
      id: 'testJourneysMenuItem',
      label: 'Test Journeys',
      icon: <TacticIcon />,
      tooltip: 'Test complex features',
      showProLabel: true,
    },
    { path: "/test-cases", id: "testCasesMenuItem", label: "Test Cases", icon: <ListAltOutlinedIcon />, tooltip: 'Manage your test cases' },
    { path: '/tasks', id: 'tasksMenuItem', label: 'Tasks', icon: <AssignmentOutlinedIcon />, total: processingTasksTotal, tooltip: 'Track Treegress task activity.' },
    ...(prototypesEnabled
      ? [
          {
            path: '/page-prototypes',
            id: 'pagePrototypesMenuItem',
            label: 'Page prototypes',
            icon: <AccountTreeOutlinedIcon />,
          },
        ]
      : []),
  ]

  const externalMenuItems = [
    {
      url: "https://docs.google.com/document/d/1Q3PjVYSltxqaxRGS5unjdbxBB9VsbjZ7fp3uOtoHcGM/edit?usp=sharing",
      label: "User Guide",
      icon: <HelpOutlineIcon />,
    },
    {
      url: "https://treegress.canny.io/",
      label: "Product Roadmap",
      icon: <RouteOutlinedIcon />,
    },
    {
      url: "https://tally.so/r/nPBNvQ",
      label: "Bug Report",
      icon: <BugReportOutlinedIcon />,
    },
    {
      url: "https://docs.google.com/forms/d/e/1FAIpQLSfAgjxu7suMjfmAiZgM6kJjNtLslJQ_cl_RI31DBsn0zctETg/viewform?usp=dialog",
      label: "Share Feedback & Win",
      icon: <TipsAndUpdatesOutlinedIcon />,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        let res
        if (selectedProject?.id) {
          res = await TaskService.getAllTasksByProjectId(selectedProject?.id)
        } else {
          if (sessionStorage.getItem('projectSelectionCleared') === 'true') {
            setProcessingTasksTotal(0)
            return
          }
          const firstProject = await ProjectService.getAllProjects(1, 1)
          if (firstProject.projects?.[0]) {
            setSelectedProject(firstProject.projects[0])
            res = await TaskService.getAllTasksByProjectId(firstProject.projects[0].id)
          }
        }
        if (res?.tasks) {
          const tasks = res.tasks.sort(
            (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
          );
          setTasks(tasks);
        }
        const newProcessingTasksTotal = res?.tasks.filter(task => task.status === 'in_progress').length
        setProcessingTasksTotal(newProcessingTasksTotal)
      } catch (error) {
        console.error('Error fetching tasks:', error)
      }
    }

    fetchData()

    const handleTaskCompleted = async () => {
      fetchData()
    }

    const handleTaskActivated = async () => {
      fetchData()
    }

    const handleTaskFailed = async () => {
      fetchData()
    }

    socketEventEmitter.on('taskCompleted', handleTaskCompleted)
    socketEventEmitter.on('taskActivated', handleTaskActivated)
    socketEventEmitter.on('taskFailed', handleTaskFailed)

    return () => {
      socketEventEmitter.off('taskCompleted', handleTaskCompleted)
      socketEventEmitter.off('taskActivated', handleTaskActivated)
      socketEventEmitter.off('taskFailed', handleTaskFailed)
    }
    // eslint-disable-next-line
  }, [selectedProject?.id])

  const formatTotal = (total) => {
    return total >= 1000 ? `${Math.floor(total / 1000)}k` : total
  }

  const handleOpenExternalLink = (url) => {
    if (url !== '/tutorial') {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      navigate('/tutorial')
    }
  }

  return (
    <Box
      className="sidebar"
      sx={{
        width: collapsedSidebar ? '64px' : '256px',
        minWidth: collapsedSidebar ? '64px' : '256px',
      }}
    >
      <Box>
        {menuItems.map((item) => {
          const isClickable = Boolean(selectedProject?.id)

          return (
            <Link
              to={isClickable ? item.path : '#'}
              onClick={(event) => {
                if (!isClickable) {
                  event.preventDefault()
                }
              }}
              key={item.label}
              id={item.id}
              style={{ textDecoration: 'none' }}
            >
              <Tooltip
                title={isClickable ? item.tooltip || '' : 'Select a project first'}
                placement="right"
                arrow
                disableHoverListener={isClickable && !item.tooltip}
              >
                <Typography
                  className="sidebarMenuItem"
                  sx={{
                    width: collapsedSidebar ? '48px' : '240px',
                    justifyContent: collapsedSidebar ? 'center' : 'flex-start',
                    bgcolor: location.pathname === item.path && isClickable ? 'rgba(255, 255, 255, 0.1) !important' : 'transparent',
                    opacity: isClickable ? 1 : 0.5,
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': {
                      bgcolor: isClickable ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }
                  }}
                >
                  {item.icon}
                  {collapsedSidebar ? null : (
                    <>
                      <span className="sidebarMenuItemLabel">{item.label}</span>
                      {item.showProLabel ? <span className="proMenuBadge">Pro</span> : null}
                    </>
                  )}
                  {collapsedSidebar && item.total ?
                    <span className='tasksMenuCollapsedBadge'>{formatTotal(item.total)}</span> :
                    !collapsedSidebar && item.total ?
                      <span className='tasksMenuExpandedBadge'>{formatTotal(item.total)}</span> :
                      null
                  }
                </Typography>
              </Tooltip>
            </Link>
          )
        })}
      </Box>
      <Box sx={{ mb: '20px' }}>
        {externalMenuItems.map((item) => {
          return (
            <Typography
              key={item.label}
              className="sidebarMenuBottomItem"
              onClick={() => handleOpenExternalLink(item.url)}
              sx={{
                width: collapsedSidebar ? '48px' : '240px',
                justifyContent: collapsedSidebar ? 'center' : 'flex-start',
              }}
            >
              {item.icon}
              {collapsedSidebar ? null : <span className="sidebarMenuItemLabel">{item.label}</span>}
            </Typography>
          )
        })}
      </Box>
    </Box>
  )
}

export default Sidebar
